import { SystemMessageDtoIn } from '@/models/in/SystemMessageDtoIn';

const STORAGE_KEY = 'systemMessagesSeen';

/**
 * The popup's old per-browser dismissal record, kept only long enough to hand it to the server.
 *
 * Until HAT-1340 this list WAS the popup's memory: a message the user closed was written here and
 * never shown again - on that browser. Signing in from a second computer brought every announcement
 * back, which is what the coordinator of fund 4481 reported after marking both of them read.
 *
 * The record now lives in NetCoreSystemMessageReads, one row per (account, message). This module
 * exists so the move does not cost every user one last popup: roughly four thousand accounts have
 * dismissed today's announcements in a browser and nowhere else, and without carrying that across
 * the deploy would put all of them back on screen.
 *
 * Delete it once no browser can still be holding the key - one validity window past the release is
 * enough, since a record only ever suppresses messages that are still live.
 */

/** The identity the old record used: the message plus the moment it last changed. */
const seenKeyOf = (message: SystemMessageDtoIn): string => `${message.id}:${message.updatedAt ?? message.createdAt}`;

const readSeenKeys = (): string[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : null;

    return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
  } catch {
    // A corrupted or unavailable store costs the user one popup they had already dismissed, which
    // is not worth failing the migration over.
    return [];
  }
};

/**
 * The live messages this browser had already dismissed, as ids the server can be told about.
 *
 * Reading does not clear the record - only a server that has accepted the ids may do that, or a
 * failed request would lose the dismissal in both places at once.
 */
export const legacySeenMessageIds = (liveMessages: SystemMessageDtoIn[]): number[] => {
  const seen = new Set(readSeenKeys());

  return liveMessages.filter((message) => seen.has(seenKeyOf(message))).map((message) => message.id);
};

/** Called once the server holds the same information. */
export const clearLegacySeenRecord = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do: the next load reads the same keys, finds them already marked read on the
    // server, and writes nothing.
  }
};
